import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import propertyService from '../../services/propertyService';

import pdfExport from '../../services/pdfExport';
import NavigationHeader from '../../components/ui/NavigationHeader';
import SubscriptionGate from '../../components/ui/SubscriptionGate';
import KPICard from './components/KPICard';
import PropertyHeader from './components/PropertyHeader';
import CashflowChart from './components/CashflowChart';
import EquityChart from './components/EquityChart';
import ROIChart from './components/ROIChart';
import NetWorthChart from './components/NetWorthChart';
import ProjectionTable from './components/ProjectionTable';
import PhotoGallery from './components/PhotoGallery';
import DossierTab from './components/DossierTab';


import PropertyAIAnalysis from './components/PropertyAIAnalysis';
import AIAnalysisSummary from './components/AIAnalysisSummary';
import StressTestsAI from './components/StressTestsAI';
import InsightsPanel from './components/InsightsPanel';
import KPISelector from './components/KPISelector';
import CombinedChart from './components/CombinedChart';
import DataValidationAlert from './components/DataValidationAlert';
import HeroVerdictCard from './components/HeroVerdictCard';
import RiskIndicator from './components/RiskIndicator';
import SimplifiedOverview from './components/SimplifiedOverview';
import ShareModal from './components/ShareModal';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CurrencySelector from '../../components/ui/CurrencySelector';
import { stripeService } from '../../services/stripe';
import { supabase } from '../../lib/supabase';
import { calculateMortgagePayment, calculateMortgageBalance, getPaymentsMadeAfterYears } from '../../utils/mortgageCalculations';


const AnalysisResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile, refreshProfile, loading: authLoading } = useAuth();
  const { formatCurrency, formatPercent } = useCurrency();
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [showSubscriptionGate, setShowSubscriptionGate] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);

  const [selectedProjectionChart, setSelectedProjectionChart] = useState('equity'); // 'equity', 'cashflow', 'networth', 'roi'
  const [selectedKPIs, setSelectedKPIs] = useState({});
  const [showAIGenerateButton, setShowAIGenerateButton] = useState(false);

  const [aiAnalysis, setAiAnalysis] = useState(null); // For visitor: AI analysis of shared property



  // Refresh profile on mount to ensure subscription tier is up to date
  // Only refresh once on mount, not on every user change
  useEffect(() => {
    if (user?.id && refreshProfile) {
      refreshProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update subscription tier from userProfile (normalize)
  useEffect(() => {
    const raw = userProfile?.subscription_tier || 'free';
    setSubscriptionTier(stripeService.normalizeTier(raw));
  }, [userProfile?.subscription_tier]);

  // Visitor mode = open same page via URL with token (no edit buttons)
  const shareToken = searchParams?.get('token');
  const propertyIdParam = searchParams?.get('propertyId');
  const isVisitor = Boolean(shareToken);

// ✅ Google Ads "conversion" quand on arrive sur /analysis-results
const gtagResultsFiredRef = { current: false }; // si tu n'as pas déjà un useRef dans ton composant

useEffect(() => {
  // Ne pas compter les visiteurs via lien partagé
  if (isVisitor) return;

  // On veut tirer la conversion UNE fois par propriété
  const propertyId = propertyIdParam || searchParams?.get('propertyId');
  if (!propertyId) return;

  const key = `gtag:analysis_completed:${propertyId}`;

  // Anti-double (même propertyId)
  if (typeof window !== 'undefined') {
    const alreadyFired = window.sessionStorage.getItem(key);
    if (alreadyFired === '1') return;
  }

  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // Optionnel: event custom pour debug/analytics
    window.gtag('event', 'analysis_results_view', {
      page_path: window.location.pathname + window.location.search,
      property_id: propertyId
    });

    // ✅ Conversion Google Ads (celle qui compte)
    window.gtag('event', 'conversion', {
      send_to: 'AW-16585798234/cdjPCPmC3PUbENrk3OQ9'
    });

    // Marque comme tiré
    window.sessionStorage.setItem(key, '1');
  }
}, [isVisitor, propertyIdParam, searchParams]);
  
  // Load property: by token (visitor) or by propertyId (owner)
  useEffect(() => {
    const loadProperty = async () => {
      if (shareToken) {
        // Visitor: load shared property by token (same row as owner)
        try {
          const result = await propertyService.getByShareToken(shareToken);
          if (!result?.success || !result?.data) {
            throw new Error(result?.error || 'Analyse non trouvée ou lien expiré');
          }
          const data = result.data;
          setProperty(data);
          // Load full AI analysis for visitor (exclude stress_tests / scenario_comparison)
          const otherTypes = ['stress_tests', 'scenario_comparison'];
          const { data: aiRows } = await supabase
            .from('property_ai_analyses')
            .select('analysis_data')
            .eq('property_id', data.id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(20);
          const fullAnalysis = (aiRows || []).find((r) => {
            const t = r?.analysis_data?.type;
            return !t || !otherTypes.includes(t);
          });
          if (fullAnalysis?.analysis_data) setAiAnalysis(fullAnalysis.analysis_data);
        } catch (err) {
          console.error('Error loading shared property:', err);
          setError(err?.message || 'Erreur lors du chargement');
        } finally {
          setLoading(false);
        }
        return;
      }

      const propertyId = propertyIdParam;
      if (!propertyId) {
        setError('Aucune propriété spécifiée');
        setLoading(false);
        return;
      }

      if (authLoading) return;
      if (!user?.id) {
        setError('Vous devez être connecté pour voir cette analyse');
        setLoading(false);
        return;
      }

      try {
        const result = await propertyService?.getById(propertyId);
        if (!result?.success) {
          throw new Error(result?.error || 'Erreur lors du chargement');
        }
        setProperty(result?.data);
      } catch (err) {
        console.error('Error loading property:', err);
        setError(err?.message || 'Erreur lors du chargement de la propriété');
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [shareToken, propertyIdParam, searchParams, user, authLoading]);



  // Calculate KPIs from property data
  const calculateKPIs = () => {
    if (!property) return null;

    // Calculate total_monthly_revenue from unit_revenues if total is missing or zero
    let totalMonthlyRevenue = property?.total_monthly_revenue || 0;
    if ((!totalMonthlyRevenue || totalMonthlyRevenue === 0) && property?.unit_revenues?.length > 0) {
      totalMonthlyRevenue = property.unit_revenues.reduce((sum, unit) => {
        return sum + (parseFloat(unit.monthly_rent) || 0);
      }, 0);
    }

    const monthlyCashflow = property?.monthly_cashflow || 0;
    const capRate = property?.cap_rate || 0;
    const noi = property?.noi || 0;
    const totalInvestment = property?.total_initial_investment || property?.down_payment_amount || 0;
    const downPayment = property?.down_payment_amount || 0;
    const annualCashflow = monthlyCashflow * 12;
    const cashOnCashReturn = totalInvestment > 0 ? (annualCashflow / totalInvestment) * 100 : 0;
    const returnOnDownPayment = downPayment > 0 ? (annualCashflow / downPayment) * 100 : 0;

    // Annual revenue and expenses
    const annualRevenue = totalMonthlyRevenue * 12;
    const annualExpenses = (property?.total_monthly_expenses || 0) * 12;

    // DSCR (Debt Service Coverage Ratio) = NOI / Annual Debt Service
    // Calculate payment from mortgage parameters based on payment frequency
    const mortgageAmountForDSCR = property?.mortgage_amount || 0;
    const interestRateForDSCR = property?.interest_rate || 0;
    const amortizationYearsForDSCR = property?.amortization_period || 25;
    const paymentFrequencyForDSCR = property?.payment_frequency || 'monthly';

    const mortgagePaymentForDSCR = calculateMortgagePayment(
      mortgageAmountForDSCR,
      interestRateForDSCR,
      amortizationYearsForDSCR,
      paymentFrequencyForDSCR
    );
    const annualMortgagePaymentForDSCR = mortgagePaymentForDSCR.annualPayment;
    const dscr = annualMortgagePaymentForDSCR > 0 ? (noi / annualMortgagePaymentForDSCR) : 0;

    // Expense ratio (dépenses / revenus)
    const expenseRatio = annualRevenue > 0 ? (annualExpenses / annualRevenue) * 100 : 0;

    // Simple payback period calculation
    const paybackPeriod = totalInvestment > 0 && annualCashflow > 0
      ? totalInvestment / annualCashflow
      : 0;

    // ROI estimation with cumulative cashflow over full projection period
    const roiLimits = stripeService?.getTierLimits(subscriptionTier);
    const roiYears = roiLimits?.projectionYears || 30;
    const appreciationRate = property?.appreciation_rate || 3;
    const rentGrowth = (property?.rent_growth_rate || 2) / 100;
    const expenseGrowth = 0.02; // 2% expense growth per year
    const baseRevenue = totalMonthlyRevenue * 12;
    const baseExpenses = (property?.total_monthly_expenses || 0) * 12;
    const amortizationYearsForROI = property?.amortization_period || 25;

    // Calculate cumulative cashflow over projection period
    const paymentFrequencyForROI = property?.payment_frequency || 'monthly';
    const mortgageAmountForROI = property?.mortgage_amount || 0;
    const interestRateForROI = property?.interest_rate || 0;

    const mortgagePaymentForROI = calculateMortgagePayment(
      mortgageAmountForROI,
      interestRateForROI,
      amortizationYearsForROI,
      paymentFrequencyForROI
    );
    const annualMortgagePaymentForROI = mortgagePaymentForROI.annualPayment;

    const cumulativeCashflowProjection = Array.from({ length: roiYears }, (_, j) => {
      const yRevenue = baseRevenue * Math.pow(1 + rentGrowth, j);
      const yExpenses = baseExpenses * Math.pow(1 + expenseGrowth, j);
      const yNOI = yRevenue - yExpenses;
      const yActualMortgagePayment = ((j + 1) > amortizationYearsForROI) ? 0 : annualMortgagePaymentForROI;
      return yNOI - yActualMortgagePayment;
    }).reduce((sum, cf) => sum + (cf || 0), 0);

    const futureValue = property?.purchase_price * Math.pow(1 + appreciationRate / 100, roiYears);
    const equityGain = futureValue - property?.purchase_price;
    const roiTotal = totalInvestment > 0
      ? ((cumulativeCashflowProjection + equityGain) / totalInvestment) * 100
      : 0;

    // Total projected return
    const totalProjectedReturn = roiTotal;

    // Break-even point: when cashflow becomes positive
    let breakEvenYears = 0;
    if (annualCashflow < 0) {
      const rentGrowth = property?.rent_growth_rate || 2;
      const expenseGrowth = 2; // 2% expense growth
      const breakEvenAmortizationYears = property?.amortization_period || 25;
      const breakEvenPaymentFrequency = property?.payment_frequency || 'monthly';
      const breakEvenMortgageAmount = property?.mortgage_amount || 0;
      const breakEvenInterestRate = property?.interest_rate || 0;

      // Calculate annual mortgage payment for break-even calculation
      const breakEvenMortgagePayment = calculateMortgagePayment(
        breakEvenMortgageAmount,
        breakEvenInterestRate,
        breakEvenAmortizationYears,
        breakEvenPaymentFrequency
      );
      const breakEvenAnnualMortgagePayment = breakEvenMortgagePayment.annualPayment;

      let currentRevenue = annualRevenue;
      let currentExpenses = annualExpenses;
      // Check if mortgage is still active (year 1, which is year index 0)
      // Year 1 = paymentsPerYear payments made, so we check if 1 > amortizationYears
      const currentMortgagePayment = (1 > breakEvenAmortizationYears) ? 0 : breakEvenAnnualMortgagePayment;
      let currentCashflow = currentRevenue - currentExpenses - currentMortgagePayment;
      let years = 0;

      while (currentCashflow < 0 && years < 30) {
        years++;
        // Compound growth: revenue and expenses grow each year
        currentRevenue = currentRevenue * (1 + rentGrowth / 100);
        currentExpenses = currentExpenses * (1 + expenseGrowth / 100);
        // Mortgage payment stops after amortization period
        // years represents the year number (1, 2, 3...), so we check if years > amortizationYears
        const yearMortgagePayment = (years > breakEvenAmortizationYears) ? 0 : breakEvenAnnualMortgagePayment;
        currentCashflow = currentRevenue - currentExpenses - yearMortgagePayment;
      }
      breakEvenYears = years;
    }

    // Interest rate sensitivity: calculate cashflow with +1% and -1% interest rate
    const currentInterestRate = property?.interest_rate || 0;
    const mortgageAmountForSensitivity = property?.mortgage_amount || 0;
    const paymentFrequencyForSensitivity = property?.payment_frequency || 'monthly';
    const amortizationYearsForSensitivity = property?.amortization_period || 25;

    const calculatePaymentAtRate = (rate) => {
      if (mortgageAmountForSensitivity === 0) return 0;
      const payment = calculateMortgagePayment(
        mortgageAmountForSensitivity,
        rate,
        amortizationYearsForSensitivity,
        paymentFrequencyForSensitivity
      );
      return payment.annualPayment; // Return annual payment
    };

    const paymentAtPlus1 = calculatePaymentAtRate(currentInterestRate + 1);
    const paymentAtMinus1 = calculatePaymentAtRate(currentInterestRate - 1);
    // calculatePaymentAtRate already returns annual payment, no need to multiply by 12
    const cashflowAtPlus1 = annualRevenue - annualExpenses - paymentAtPlus1;
    const cashflowAtMinus1 = annualRevenue - annualExpenses - paymentAtMinus1;
    const interestSensitivity = {
      plus1: cashflowAtPlus1 - annualCashflow,
      minus1: cashflowAtMinus1 - annualCashflow
    };

    // Vacancy sensitivity: calculate cashflow with +5% and -5% vacancy
    // Note: These calculations assume current year (year 0), so mortgage is still active
    // Use the DSCR annual mortgage payment which is already calculated with correct frequency
    const vacancyRate = property?.vacancy_rate || 5;
    const revenueAtPlus5Vacancy = annualRevenue * (1 - (vacancyRate + 5) / 100);
    const revenueAtMinus5Vacancy = annualRevenue * (1 - Math.max(0, vacancyRate - 5) / 100);
    const cashflowAtPlus5Vacancy = revenueAtPlus5Vacancy - annualExpenses - annualMortgagePaymentForDSCR;
    const cashflowAtMinus5Vacancy = revenueAtMinus5Vacancy - annualExpenses - annualMortgagePaymentForDSCR;
    const vacancySensitivity = {
      plus5: cashflowAtPlus5Vacancy - annualCashflow,
      minus5: cashflowAtMinus5Vacancy - annualCashflow
    };

    return {
      monthlyCashflow,
      annualCashflow,
      capRate,
      roiTotal,
      paybackPeriod,
      cashOnCashReturn,
      returnOnDownPayment,
      noi,
      dscr,
      expenseRatio,
      totalProjectedReturn,
      breakEvenYears,
      interestSensitivity,
      vacancySensitivity
    };
  };

  // Generate projection data
  const generateProjectionData = () => {
    if (!property) return [];

    const rentGrowth = (property?.rent_growth_rate || 2) / 100;
    const appreciation = (property?.appreciation_rate || 3) / 100;
    const expenseGrowth = 0.02; // 2% expense growth per year

    // Calculate total_monthly_revenue from unit_revenues if total is missing or zero
    let totalMonthlyRevenue = property?.total_monthly_revenue || 0;
    if ((!totalMonthlyRevenue || totalMonthlyRevenue === 0) && property?.unit_revenues?.length > 0) {
      totalMonthlyRevenue = property.unit_revenues.reduce((sum, unit) => {
        return sum + (parseFloat(unit.monthly_rent) || 0);
      }, 0);
    }

    // Base values (annual)
    const baseRevenue = totalMonthlyRevenue * 12;
    const baseExpenses = (property?.total_monthly_expenses || 0) * 12;

    // Base detailed expenses (monthly to annual)
    const basePropertyTaxesAnnual = (property?.property_taxes || 0) * 12;
    const baseInsuranceAnnual = (property?.insurance || 0) * 12;
    const baseMaintenanceAnnual = (property?.maintenance || 0) * 12;
    const baseOtherExpensesAnnual = (property?.other_expenses || 0) * 12;

    const purchasePrice = property?.purchase_price || 0;
    const mortgageAmount = property?.mortgage_amount || 0;
    const amortizationYears = property?.amortization_period || 25;
    const interestRate = property?.interest_rate || 0;
    const paymentFrequency = property?.payment_frequency || 'monthly';
    const downPayment = property?.down_payment_amount || 0;
    const totalInitialInvestment = property?.total_initial_investment || downPayment;
    const managementFeePercent = property?.management_fee_percent || 0;

    // Calculate mortgage payment using utility function
    const mortgagePaymentCalc = calculateMortgagePayment(
      mortgageAmount,
      interestRate,
      amortizationYears,
      paymentFrequency
    );

    const periodicPayment = mortgagePaymentCalc.periodicPayment;
    const annualMortgagePayment = mortgagePaymentCalc.annualPayment;
    const paymentsPerYear = mortgagePaymentCalc.paymentsPerYear;
    const periodicRate = mortgagePaymentCalc.periodicRate;
    const totalPayments = mortgagePaymentCalc.totalPayments;

    // For backward compatibility and calculations, also calculate monthly equivalent
    const monthlyPayment = annualMortgagePayment / 12;

    // Determine projection years based on subscription tier
    const limits = stripeService?.getTierLimits(subscriptionTier);
    const projectionYears = limits?.projectionYears || 30;

    // Create array starting with year 0 (purchase) + projection years (1 to projectionYears)
    const allYears = [];

    // Year 0: Purchase year - On calcule le cashflow comme si c'était une année normale
    // pour avoir une cohérence avec les années suivantes
    const year0TotalExpenses = baseExpenses > 0 ? baseExpenses : (basePropertyTaxesAnnual + baseInsuranceAnnual + baseMaintenanceAnnual + baseOtherExpensesAnnual + (baseRevenue * (managementFeePercent / 100)));
    const year0NOI = baseRevenue - year0TotalExpenses;
    // Le cashflow de l'année 0 doit aussi inclure le paiement hypothécaire pour être cohérent
    const year0Cashflow = year0NOI - annualMortgagePayment;

    allYears.push({
      year: 0,
      paymentsMade: 0,
      rentalIncome: baseRevenue,
      totalIncome: baseRevenue,
      propertyTaxes: basePropertyTaxesAnnual,
      insurance: baseInsuranceAnnual,
      maintenance: baseMaintenanceAnnual,
      otherExpenses: baseOtherExpensesAnnual,
      management: baseRevenue * (managementFeePercent / 100),
      totalExpenses: year0TotalExpenses,
      noi: year0NOI,
      actualMortgagePayment: annualMortgagePayment, // Inclure le paiement hypothécaire
      annualCashflow: year0Cashflow, // Cashflow après hypothèque
      monthlyCashflow: year0Cashflow / 12,
      mortgageBalance: mortgageAmount,
      propertyValue: purchasePrice,
      equity: Math.max(0, purchasePrice - mortgageAmount),
      previousYearPropertyValue: purchasePrice,
      previousYearMortgageBalance: mortgageAmount,
      previousYearEquity: Math.max(0, purchasePrice - mortgageAmount),
      propertyAppreciationThisYear: 0,
      annualROI: 0,
      cumulativeCashflow: year0Cashflow,
      totalEquityGain: Math.max(0, purchasePrice - mortgageAmount) - downPayment,
      totalReturn: year0Cashflow + (Math.max(0, purchasePrice - mortgageAmount) - downPayment),
      cumulativeROI: totalInitialInvestment > 0 ? ((year0Cashflow + (Math.max(0, purchasePrice - mortgageAmount) - downPayment)) / totalInitialInvestment) * 100 : 0,
      otherIncome: 0,
      mortgagePayment: annualMortgagePayment
    });

    // Years 1 to projectionYears: Full years with payments
    // Year 1 (i=0) represents the first full year after purchase (12 payments made)
    for (let i = 0; i < projectionYears; i++) {
      // Year indexing: i=0 is year 1 (first full year of payments)
      // At year 1 (i=0), we've made 12 payments (one full year)
      const year = i + 1;

      // Number of payments made: at year 1 (i=0), we've made paymentsPerYear payments
      // Year 1 = paymentsPerYear payments, Year 2 = 2 * paymentsPerYear payments, etc.
      // Use utility function to ensure consistency
      const paymentsMade = getPaymentsMadeAfterYears(i + 1, paymentFrequency);

      // Revenues (growing with compound growth)
      // Year 1 (i=0): baseRevenue * (1 + rentGrowth)^1 = baseRevenue * 1.02 (first year of growth)
      // Year 2 (i=1): baseRevenue * (1 + rentGrowth)^2 = baseRevenue * 1.02^2 (second year of growth)
      const rentalIncome = baseRevenue * Math.pow(1 + rentGrowth, i + 1);
      const totalIncome = rentalIncome;

      // Calculate detailed expenses for this year (growing with compound expense growth rate)
      // Year 1 (i=0): first year of growth, Year 2 (i=1): second year of growth
      const propertyTaxes = basePropertyTaxesAnnual * Math.pow(1 + expenseGrowth, i + 1);
      const insurance = baseInsuranceAnnual * Math.pow(1 + expenseGrowth, i + 1);
      const maintenance = baseMaintenanceAnnual * Math.pow(1 + expenseGrowth, i + 1);
      const otherExpenses = baseOtherExpensesAnnual * Math.pow(1 + expenseGrowth, i + 1);

      // Management fee is a percentage of revenue (grows with revenue)
      const management = rentalIncome * (managementFeePercent / 100);

      // Calculate total expenses from detailed breakdown
      const calculatedTotalExpenses = propertyTaxes + insurance + maintenance + otherExpenses + management;

      // Use calculated total if baseExpenses is missing or zero, otherwise use the base with growth
      const totalExpenses = (baseExpenses > 0)
        ? baseExpenses * Math.pow(1 + expenseGrowth, i + 1)
        : calculatedTotalExpenses;

      // NOI and Cashflow
      const noi = totalIncome - totalExpenses;

      // Check if mortgage is fully paid (after amortization period)
      // Year 1 (i=0): paymentsMade = 12 (one year of payments)
      // Year 25 (i=24): paymentsMade = 300 (25 years of payments)
      // Year 26 (i=25): paymentsMade = 312, but totalPayments = 300, so mortgage is paid
      const isMortgagePaid = paymentsMade >= totalPayments || (i + 1 > amortizationYears);

      // Mortgage payment stops after amortization period
      const actualMortgagePayment = isMortgagePaid ? 0 : annualMortgagePayment;
      const annualCashflow = noi - actualMortgagePayment;
      const monthlyCashflow = annualCashflow / 12;

      // Mortgage balance calculation using utility function
      const mortgageBalance = calculateMortgageBalance(
        mortgageAmount,
        interestRate,
        amortizationYears,
        paymentFrequency,
        paymentsMade
      );

      // Property value and equity (compound appreciation)
      // Year 1 (i=0): purchasePrice * (1 + appreciation)^1 = purchasePrice * 1.03 (first year of appreciation)
      // Year 2 (i=1): purchasePrice * (1 + appreciation)^2 = purchasePrice * 1.03^2 (second year of appreciation)
      const propertyValue = purchasePrice * Math.pow(1 + appreciation, i + 1);
      const equity = Math.max(0, propertyValue - mortgageBalance);

      // Calculate previous year's equity for annual ROI calculation
      // Year 1 (i=0): previous year is year 0 (purchase), so property value = purchasePrice
      // Year 2 (i=1): previous year is year 1, so property value = purchasePrice * (1 + appreciation)^1
      const previousYearPropertyValue = purchasePrice * Math.pow(1 + appreciation, i);

      // Calculate previous year's mortgage balance for ROI calculation
      // Year 1 (i=0): previous year is year 0, so prevPaymentsMade = 0 (no payments yet)
      // Year 2 (i=1): previous year is year 1, so prevPaymentsMade = paymentsPerYear (one year of payments)
      const prevPaymentsMade = getPaymentsMadeAfterYears(i, paymentFrequency);
      const previousYearMortgageBalance = calculateMortgageBalance(
        mortgageAmount,
        interestRate,
        amortizationYears,
        paymentFrequency,
        prevPaymentsMade
      );
      const previousYearEquity = Math.max(0, previousYearPropertyValue - previousYearMortgageBalance);

      // Annual ROI calculation: (annual cashflow + property appreciation) / initial investment
      // We use property appreciation only (not full equity gain) to avoid artificial spikes
      // when mortgage is paid off. The mortgage principal reduction is already reflected
      // in the increased cashflow after mortgage payoff.
      const propertyAppreciationThisYear = propertyValue - previousYearPropertyValue;

      // Annual ROI: cashflow + property appreciation (not including mortgage principal reduction)
      // This gives a smoother, more accurate representation of annual returns
      const annualROI = totalInitialInvestment > 0
        ? ((annualCashflow + propertyAppreciationThisYear) / totalInitialInvestment) * 100
        : 0;

      // Cumulative ROI calculation
      // Include year 0 cashflow (no mortgage payment) + all years 1 to current year
      const cumulativeCashflow = year0NOI + Array.from({ length: i + 1 }, (_, j) => {
        // Year j+1: apply (j+1) years of growth
        const yRevenue = baseRevenue * Math.pow(1 + rentGrowth, j + 1);
        const yPropertyTaxes = basePropertyTaxesAnnual * Math.pow(1 + expenseGrowth, j + 1);
        const yInsurance = baseInsuranceAnnual * Math.pow(1 + expenseGrowth, j + 1);
        const yMaintenance = baseMaintenanceAnnual * Math.pow(1 + expenseGrowth, j + 1);
        const yOtherExpenses = baseOtherExpensesAnnual * Math.pow(1 + expenseGrowth, j + 1);
        const yManagement = yRevenue * (managementFeePercent / 100);
        const yExpenses = (baseExpenses > 0)
          ? baseExpenses * Math.pow(1 + expenseGrowth, j + 1)
          : yPropertyTaxes + yInsurance + yMaintenance + yOtherExpenses + yManagement;
        const yNOI = yRevenue - yExpenses;
        // Check if mortgage is paid for this year
        // Year index j is 0-based: j=0 is year 1
        // If amortization is 25 years, payments stop after year 25
        const yPaymentsMade = getPaymentsMadeAfterYears(j + 1, paymentFrequency);
        const yIsMortgagePaid = yPaymentsMade >= totalPayments || ((j + 1) > amortizationYears);
        const yActualMortgagePayment = yIsMortgagePaid ? 0 : annualMortgagePayment;
        return yNOI - yActualMortgagePayment;
      }).reduce((sum, cf) => sum + (cf || 0), 0);

      const totalEquityGain = equity - downPayment;
      const totalReturn = cumulativeCashflow + totalEquityGain;
      const cumulativeROI = totalInitialInvestment > 0
        ? (totalReturn / totalInitialInvestment) * 100
        : 0;

      // Other income (can be added later if needed)
      const otherIncome = 0;

      allYears.push({
        year,
        rentalIncome,
        totalIncome,
        totalExpenses,
        noi,
        mortgagePayment: actualMortgagePayment,
        monthlyCashflow,
        annualCashflow,
        propertyValue,
        mortgageBalance,
        equity,
        annualROI,
        cumulativeROI,
        propertyTaxes,
        insurance,
        maintenance,
        management,
        otherExpenses,
        otherIncome
      });
    }

    return allYears;
  };

  const kpis = calculateKPIs();
  const projectionData = generateProjectionData();
  const limits = stripeService?.getTierLimits(subscriptionTier);

  const mockCashflowData = projectionData?.map(p => ({
    year: p?.year,
    monthlyCashflow: p?.monthlyCashflow,
    annualCashflow: p?.annualCashflow
  }));

  const mockEquityData = projectionData?.map(p => ({
    year: p?.year,
    propertyValue: p?.propertyValue,
    mortgageBalance: p?.mortgageBalance,
    equity: p?.equity
  }));

  const mockROIData = projectionData?.map(p => ({
    year: p?.year,
    annualROI: p?.annualROI || 0,
    cumulativeROI: p?.cumulativeROI || 0
  }));

  // Net worth data (equity only)
  const mockNetWorthData = projectionData?.map(p => ({
    year: p?.year,
    netWorth: p?.equity || 0
  }));

  // Combined chart data
  const combinedChartData = projectionData?.map(p => ({
    year: p?.year,
    propertyValue: p?.propertyValue || 0,
    mortgageBalance: p?.mortgageBalance || 0,
    annualROI: p?.annualROI || 0
  }));

  // Available KPIs for selector
  const availableKPIs = [
    { key: 'noi', label: "Revenu net d'exploitation (NOI)", icon: 'FileText' },
    { key: 'monthlyCashflow', label: 'Cashflow mensuel', icon: 'DollarSign' },
    { key: 'annualCashflow', label: 'Cashflow annuel', icon: 'TrendingUp' },
    { key: 'capRate', label: 'Cap rate', icon: 'Percent' },
    { key: 'cashOnCashReturn', label: 'Cash-on-Cash Return', icon: 'Wallet' },
    { key: 'dscr', label: 'DSCR', icon: 'ShieldCheck' },
    { key: 'expenseRatio', label: 'Ratio dépenses / revenus', icon: 'BarChart3' },
    { key: 'returnOnDownPayment', label: 'Rendement sur mise de fonds', icon: 'Crosshair' },
    { key: 'totalProjectedReturn', label: 'Rendement total projeté', icon: 'TrendingUp' },
    { key: 'breakEvenYears', label: 'Point mort', icon: 'Flag' },
    { key: 'paybackPeriod', label: 'Période de récupération', icon: 'Clock' }
  ];

  // Helper function to get KPI value
  const getKPIValue = (key) => {
    switch (key) {
      case 'noi': return formatCurrency(kpis?.noi);
      case 'monthlyCashflow': return formatCurrency(kpis?.monthlyCashflow);
      case 'annualCashflow': return formatCurrency(kpis?.annualCashflow);
      case 'capRate': return formatPercent(kpis?.capRate);
      case 'cashOnCashReturn': return formatPercent(kpis?.cashOnCashReturn);
      case 'dscr': return kpis?.dscr?.toFixed(2) || '0.00';
      case 'expenseRatio': return formatPercent(kpis?.expenseRatio);
      case 'returnOnDownPayment': return formatPercent(kpis?.returnOnDownPayment);
      case 'totalProjectedReturn': return formatPercent(kpis?.totalProjectedReturn);
      case 'breakEvenYears': return kpis?.breakEvenYears === 0 ? 'Immédiat' : `${kpis?.breakEvenYears} ans`;
      case 'paybackPeriod': return `${kpis?.paybackPeriod?.toFixed(1)} ans`;
      default: return 'N/A';
    }
  };

  const getKPISubtitle = (key) => {
    const subtitles = {
      'noi': "Revenu annuel net",
      'monthlyCashflow': "Après toutes dépenses",
      'annualCashflow': "Flux de trésorerie annuel",
      'capRate': "Taux de capitalisation",
      'cashOnCashReturn': "Rendement sur investissement",
      'dscr': "Debt Service Coverage Ratio",
      'expenseRatio': "Pourcentage des dépenses",
      'returnOnDownPayment': "ROI sur mise de fonds",
      'totalProjectedReturn': `ROI sur ${(stripeService?.getTierLimits(subscriptionTier)?.projectionYears || 30)} ans`,
      'breakEvenYears': "Break-even point",
      'paybackPeriod': "Retour sur investissement"
    };
    return subtitles[key] || '';
  };

  const getKPIVariant = (key) => {
    switch (key) {
      case 'monthlyCashflow':
      case 'annualCashflow':
        return kpis?.[key] >= 0 ? "success" : "warning";
      case 'dscr':
        return kpis?.dscr >= 1.25 ? "success" : kpis?.dscr >= 1 ? "warning" : "default";
      case 'breakEvenYears':
        return kpis?.breakEvenYears === 0 ? "success" : "warning";
      default:
        return "primary";
    }
  };

  // Deduplicate photos by URL to prevent display issues
  const propertyPhotos = React.useMemo(() => {
    const photos = property?.photos || property?.images || [];
    const seen = new Set();
    return photos.filter(photo => {
      if (!photo?.url || seen.has(photo.url)) return false;
      seen.add(photo.url);
      return true;
    });
  }, [property?.photos, property?.images]);



  const handleBack = () => {
    navigate(isVisitor ? '/' : '/main-dashboard');
  };

  const handleEdit = () => {
    const propertyId = searchParams?.get('propertyId');
    if (propertyId) {
      navigate(`/property-analysis-wizard?propertyId=${propertyId}`);
    } else {
      navigate('/property-analysis-wizard');
    }
  };

  const handleExport = async () => {
    try {
      await pdfExport.exportPropertyAnalysis(property, kpis, projectionData);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    }
  };

  /** Clic sur "Partager" : publie l'analyse (si pas déjà publiée) puis ouvre la sous-fenêtre de partage. */
  const handleShare = async () => {
    if (!property?.id) return;

    try {
      let shareToken = property.share_token;
      if (!shareToken) {
        shareToken = crypto.randomUUID();
        const { data: updated, error: updateError } = await supabase
          .from('properties')
          .update({ is_shared: true, share_token: shareToken })
          .eq('id', property.id)
          .eq('user_id', user?.id)
          .select('id, share_token, is_shared')
          .maybeSingle();

        if (updateError) {
          console.error('Error enabling share:', updateError);
          const msg = updateError?.message || updateError?.code || 'Erreur inconnue';
          alert(`Impossible de publier l'analyse. ${msg}`);
          return;
        }
        if (!updated) {
          console.error('Share update: no row updated (RLS or not found)');
          alert('Impossible de publier l\'analyse. Vérifiez que vous êtes bien connecté.');
          return;
        }
        setProperty(prev => ({ ...prev, is_shared: true, share_token: updated.share_token || shareToken }));
      }
      setShowShareModal(true);
    } catch (err) {
      console.error('Error enabling share:', err);
      alert(`Impossible de publier l'analyse. ${err?.message || 'Veuillez réessayer.'}`);
    }
  };

  const handleUnshare = async () => {
    if (!property?.id) return;

    try {
      const { error: updateError } = await supabase
        .from('properties')
        .update({ is_shared: false })
        .eq('id', property.id);

      if (!updateError) {
        setProperty(prev => ({ ...prev, is_shared: false }));
      }
    } catch (err) {
      console.error('Error disabling share:', err);
    }
  };

  /** URL visiteur = page dédiée /shared?token= (vue d'ensemble + analyse avancée + IA). */
  const getShareUrl = () => {
    if (!property?.share_token) return '';
    return `${window.location.origin}/shared?token=${property.share_token}`;
  };



  const handleUpgrade = () => {
    navigate('/subscription-pricing');
  };

  const handlePlanning = () => {
    const propertyId = searchParams?.get('propertyId');
    if (propertyId) {
      navigate(`/property-planning?propertyId=${propertyId}`);
    } else {
      navigate('/property-planning');
    }
  };

  const handlePurchaseJourney = () => {
    const propertyId = searchParams?.get('propertyId');
    if (propertyId) {
      navigate(`/purchase-journey?propertyId=${propertyId}`);
    } else {
      navigate('/purchase-journey');
    }
  };

  const handleStageChange = async (propertyId, newStage) => {
    if (!propertyId || !user?.id) return;

    try {
      // Get current property data
      const currentFormData = propertyService.toFormFormat(property);

      // Update property stage
      const result = await propertyService.update(propertyId, {
        ...currentFormData,
        stage: newStage
      }, user.id);

      if (result?.success) {
        // Reload property to get updated data
        const updatedProperty = await propertyService.getById(propertyId);
        if (updatedProperty?.success) {
          setProperty(updatedProperty.data);
        }
      } else {
        console.error('Error updating property stage:', result?.error);
        alert(`Erreur lors de la mise à jour de l'étape: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error updating property stage:', error);
      alert(`Erreur lors de la mise à jour de l'étape: ${error?.message}`);
    }
  };

  const handleConvertToAcquired = async () => {
    const propertyId = searchParams?.get('propertyId');
    if (!propertyId || !user?.id) return;

    if (!window.confirm('Voulez-vous convertir cette propriété en propriété acquise ? Elle sera ajoutée à votre portefeuille acquis.')) {
      return;
    }

    try {
      // Get current property data
      const currentFormData = propertyService.toFormFormat(property);

      // Update property stage to 'acquired'
      const result = await propertyService.update(propertyId, {
        ...currentFormData,
        stage: 'acquired'
      }, user.id);

      if (result?.success) {
        // Reload property to get updated data
        const updatedProperty = await propertyService.getById(propertyId);
        if (updatedProperty?.success) {
          setProperty(updatedProperty.data);
          // Show success message
          alert('Propriété convertie en propriété acquise avec succès !');
        }
      } else {
        console.error('Error converting property:', result?.error);
        alert(`Erreur lors de la conversion: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error converting property:', error);
      alert(`Erreur lors de la conversion: ${error?.message}`);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'LayoutDashboard' },
    { id: 'projections', label: 'Analyse avancée', icon: 'TrendingUp' },
    { id: 'stress-tests', label: 'Stress tests', icon: 'Activity' },
    { id: 'photos', label: 'Photos', icon: 'Image' },
    { id: 'dossier', label: 'Dossier', icon: 'Folder' }
  ];

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      {isVisitor && (
        <div
          className="sticky top-0 z-50 flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium text-white"
          style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, #1e40af 100%)' }}
        >
          <Icon name="Eye" size={18} />
          <span>Mode visiteur — Cette analyse a été partagée avec vous</span>
        </div>
      )}
      <NavigationHeader
        subscriptionTier={subscriptionTier}
      />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8" style={{ paddingTop: isVisitor ? 'calc(64px + 2rem + 40px)' : 'calc(64px + 2rem)' }}>
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Chargement de l'analyse...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-md">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold mb-2">Erreur</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={handleBack}>{isVisitor ? 'Retour à l\'accueil' : 'Retour au tableau de bord'}</Button>
            </div>
          </div>
        ) : property && kpis ? (
          <>
            <PropertyHeader
              property={{
                address: property?.address,
                city: property?.city,
                province: property?.province,
                type: property?.property_type,
                status: property?.status,
                stage: property?.stage,
                analysisDate: property?.analysis_date,
                mainImage: property?.photos?.[0]?.url || property?.mainImage,
                mainImageAlt: property?.photos?.[0]?.alt || '',
                propertyUrl: property?.property_url,
                ...property,
                purchase_price: property?.purchase_price,
                purchasePrice: property?.purchase_price,
                price: property?.purchase_price
              }}
              kpis={kpis}
              isVisitor={isVisitor}
              subscriptionTier={subscriptionTier}
              onBack={handleBack}
              onEdit={handleEdit}
              onExport={handleExport}
              onShare={handleShare}

              onPlanning={handlePlanning}
              onPurchaseJourney={handlePurchaseJourney}
              onStageChange={handleStageChange}
              onConvertToAcquired={handleConvertToAcquired}
            />

            <div className="mb-6 sticky top-[64px] z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-2 -mx-4 px-4 md:mx-0 md:px-0 md:static md:bg-transparent border-b md:border-none border-border/50">
              <div className="flex overflow-x-auto gap-2 pb-1 items-center scrollbar-hide no-scrollbar -mx-1 px-1">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 rounded-lg whitespace-nowrap transition-colors flex-shrink-0 border text-xs md:text-sm font-medium ${activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground hover:bg-muted border-border'
                      }`}
                  >
                    <Icon name={tab?.icon} size={16} className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                    <span>{tab?.label}</span>
                  </button>
                ))}

                {/* Bouton générer par l'IA dans la navigation */}
                {activeTab === 'overview' && (
                  <div className="ml-auto hidden md:block">
                    <AIAnalysisSummary
                      property={property}
                      user={user}
                      userProfile={userProfile}
                      subscriptionTier={subscriptionTier}
                      compact={true}
                      analysis={isVisitor ? aiAnalysis : undefined}
                      isReadOnly={isVisitor}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ✅ Validation des données sous la navigation */}
            <DataValidationAlert
              property={property}
              onEdit={!isVisitor ? handleEdit : undefined}
              mode="compact"
            />

            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Analyse IA en premier - résumé court */}
                <AIAnalysisSummary
                  property={property}
                  user={user}
                  userProfile={userProfile}
                  subscriptionTier={subscriptionTier}
                  onEdit={handleEdit}
                  summaryMode={true}
                  analysis={isVisitor ? aiAnalysis : undefined}
                  isReadOnly={isVisitor}
                />

                {/* Vue d'ensemble avec score, indicateurs, forces/faiblesses et graphique */}
                <SimplifiedOverview
                  property={property}
                  kpis={kpis}
                  projectionData={projectionData}
                />
              </div>
            )}

            {activeTab === 'stress-tests' && (
              <div className="space-y-6">
                {!isVisitor && (
                  <StressTestsAI
                    property={property}
                    user={user}
                    userProfile={userProfile}
                    subscriptionTier={subscriptionTier}
                    onUpgrade={() => setShowSubscriptionGate(true)}
                  />
                )}
                {isVisitor && (
                  <div className="bg-card border border-border rounded-xl p-8 text-center">
                    <p className="text-muted-foreground">Les stress tests ne sont pas disponibles en mode visiteur.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'projections' && (
              <div className="space-y-6">
                {/* Analyse IA complète */}
                <PropertyAIAnalysis
                  property={property}
                  user={user}
                  userProfile={userProfile}
                  subscriptionTier={subscriptionTier}
                  onEdit={handleEdit}
                  analysis={isVisitor ? aiAnalysis : undefined}
                  isReadOnly={isVisitor}
                />

                {/* Header simplifié - sans répétition de l'adresse */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold text-foreground">
                      Analyse avancée
                    </h2>
                    <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-semibold rounded-full">
                      {limits?.projectionYears || 30} ans
                    </span>
                  </div>
                </div>

                {/* KPI Cards with different colors */}
                {projectionData && projectionData.length > 0 && (() => {
                  const finalYear = projectionData[projectionData.length - 1];
                  const finalEquity = finalYear?.equity || 0;
                  const finalPropertyValue = finalYear?.propertyValue || 0;
                  const finalCashflow = finalYear?.annualCashflow || 0;
                  const finalMortgage = finalYear?.mortgageBalance || 0;
                  const initialInvestment = property?.total_initial_investment || property?.down_payment_amount || 0;
                  const equityGrowth = initialInvestment > 0 ? ((finalEquity - initialInvestment) / initialInvestment) * 100 : 0;

                  // KPI additionnels (même logique que les KPI actuels)
                  const projectionYears = limits?.projectionYears || 30;
                  const cashflowCumulative = (projectionData || []).reduce((sum, y) => sum + (parseFloat(y?.annualCashflow) || 0), 0);
                  const cashflowAvg = (projectionData && projectionData.length > 0) ? (cashflowCumulative / projectionData.length) : 0;
                  const cumulativeROI = finalYear?.cumulativeROI || 0;
                  const breakEven = (projectionData || []).find((y) => (y?.year ?? 0) >= 0 && (parseFloat(y?.annualCashflow) || 0) > 0);
                  const breakEvenText = breakEven?.year === 0 ? 'Immédiat' : (breakEven?.year ? `Année ${breakEven.year}` : 'N/A');
                  const ltvFinal = finalPropertyValue > 0 ? (finalMortgage / finalPropertyValue) * 100 : 0;

                  // ROI total moyen: (cashflow cumulé + prise de valeur + paiement d'équité sur prêt) / mise de fonds / nombre d'années
                  const purchasePrice = property?.purchase_price || 0;
                  const mortgageAmount = property?.mortgage_amount || 0;
                  const valueAppreciation = finalPropertyValue - purchasePrice;
                  const principalPaydown = mortgageAmount - finalMortgage;
                  const totalROIAmount = cashflowCumulative + valueAppreciation + principalPaydown;
                  const totalROIPercent = initialInvestment > 0 ? (totalROIAmount / initialInvestment) * 100 : 0;
                  const nbYearsForAvg = finalYear?.year || projectionYears;
                  // CAGR : taux annuel composé qui donnerait le ROI total après n ans
                  // Formule : (1 + ROI_total)^(1/n) - 1
                  const totalROIDecimal = totalROIPercent / 100; // ex: 5.2 → 0.052 pour 520%
                  const avgAnnualROI = nbYearsForAvg > 0 && (1 + totalROIDecimal) > 0
                    ? (Math.pow(1 + totalROIDecimal, 1 / nbYearsForAvg) - 1) * 100
                    : 0;

                  return (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {/* Équité finale - Vert clair */}
                        <div
                          style={{
                            background: '#d1fae5',
                            border: '1px solid #86efac',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="DollarSign" size={20} style={{ color: '#16a34a' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#166534',
                                margin: 0
                              }}
                            >
                              Équité finale ({limits?.projectionYears || 30} ans)
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#166534',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatCurrency(finalEquity)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#16a34a',
                              margin: 0
                            }}
                          >
                            +{equityGrowth.toFixed(0)}% de croissance
                          </p>
                        </div>

                        {/* Valeur de la propriété - Bleu clair */}
                        <div
                          style={{
                            background: '#dbeafe',
                            border: '1px solid #93c5fd',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Home" size={20} style={{ color: '#1e40af' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#1e3a8a',
                                margin: 0
                              }}
                            >
                              Valeur de la propriété
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#1e3a8a',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatCurrency(finalPropertyValue)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#1e40af',
                              margin: 0
                            }}
                          >
                            Année {limits?.projectionYears || 30}
                          </p>
                        </div>

                        {/* Cashflow année 10 - Violet clair */}
                        <div
                          style={{
                            background: '#ede9fe',
                            border: '1px solid #c4b5fd',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="TrendingUp" size={20} style={{ color: '#6b21a8' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#581c87',
                                margin: 0
                              }}
                            >
                              Cashflow année {limits?.projectionYears || 30}
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: finalCashflow >= 0 ? '#6b21a8' : '#dc2626',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatCurrency(finalCashflow)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#7c3aed',
                              margin: 0
                            }}
                          >
                            Par année
                          </p>
                        </div>

                        {/* Hypothèque restante - Orange clair */}
                        <div
                          style={{
                            background: '#fed7aa',
                            border: '1px solid #fdba74',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Target" size={20} style={{ color: '#c2410c' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#9a3412',
                                margin: 0
                              }}
                            >
                              Hypothèque restante
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#9a3412',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatCurrency(finalMortgage)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#c2410c',
                              margin: 0
                            }}
                          >
                            {finalMortgage <= 0 ? 'Payée intégralement' : `Année ${limits?.projectionYears || 30}`}
                          </p>
                        </div>
                      </div>

                      {/* ✅ KPIs additionnels */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {/* Cashflow cumulé */}
                        <div
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Wallet" size={20} style={{ color: '#0f172a' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#0f172a',
                                margin: 0
                              }}
                            >
                              Cashflow cumulé
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#0f172a',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatCurrency(cashflowCumulative)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#334155',
                              margin: 0
                            }}
                          >
                            Total sur {projectionYears} ans
                          </p>
                        </div>

                        {/* Cashflow moyen */}
                        <div
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #e2e8f0',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="BarChart3" size={20} style={{ color: '#0f172a' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#0f172a',
                                margin: 0
                              }}
                            >
                              Cashflow moyen
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: cashflowAvg >= 0 ? '#0f172a' : '#dc2626',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatCurrency(cashflowAvg)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#334155',
                              margin: 0
                            }}
                          >
                            Par année (moyenne)
                          </p>
                        </div>

                        {/* ROI cumulatif */}
                        <div
                          style={{
                            background: '#eef2ff',
                            border: '1px solid #c7d2fe',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Percent" size={20} style={{ color: '#3730a3' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#312e81',
                                margin: 0
                              }}
                            >
                              ROI cumulatif
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#312e81',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatPercent(cumulativeROI)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#4338ca',
                              margin: 0
                            }}
                          >
                            Total projeté
                          </p>
                        </div>

                        {/* Break-even + LTV */}
                        <div
                          style={{
                            background: '#fff7ed',
                            border: '1px solid #fed7aa',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Flag" size={20} style={{ color: '#c2410c' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#9a3412',
                                margin: 0
                              }}
                            >
                              Break-even & LTV
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.1rem',
                              fontWeight: 700,
                              color: '#9a3412',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {breakEvenText} • {formatPercent(ltvFinal)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#c2410c',
                              margin: 0
                            }}
                          >
                            1er cashflow + dette/valeur
                          </p>
                        </div>
                      </div>

                      {/* ROI Total moyen */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* ROI Total */}
                        <div
                          style={{
                            background: '#ecfdf5',
                            border: '1px solid #6ee7b7',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="TrendingUp" size={20} style={{ color: '#059669' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#065f46',
                                margin: 0
                              }}
                            >
                              ROI total ({nbYearsForAvg} ans)
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#065f46',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatPercent(totalROIPercent)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#059669',
                              margin: 0
                            }}
                          >
                            Cashflow + Appréciation + Équité prêt
                          </p>
                        </div>

                        {/* ROI Total Moyen par année */}
                        <div
                          style={{
                            background: '#f0fdf4',
                            border: '1px solid #86efac',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="BarChart3" size={20} style={{ color: '#16a34a' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#166534',
                                margin: 0
                              }}
                            >
                              ROI total moyen / an
                            </p>
                          </div>
                          <p
                            style={{
                              fontFamily: 'var(--font-heading)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: '#166534',
                              margin: 0,
                              marginBottom: '4px'
                            }}
                          >
                            {formatPercent(avgAnnualROI)}
                          </p>
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: '#16a34a',
                              margin: 0
                            }}
                          >
                            ROI total ÷ {nbYearsForAvg} ans (composé)
                          </p>
                        </div>

                        {/* Détail du ROI total */}
                        <div
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="PieChart" size={20} style={{ color: '#475569' }} />
                            <p
                              style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: '#334155',
                                margin: 0
                              }}
                            >
                              Détail du rendement total
                            </p>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                              <span style={{ color: '#64748b' }}>Cashflow cumulé</span>
                              <span style={{ fontWeight: 600, color: cashflowCumulative >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(cashflowCumulative)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                              <span style={{ color: '#64748b' }}>Prise de valeur</span>
                              <span style={{ fontWeight: 600, color: '#3b82f6' }}>{formatCurrency(valueAppreciation)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                              <span style={{ color: '#64748b' }}>Équité sur prêt</span>
                              <span style={{ fontWeight: 600, color: '#8b5cf6' }}>{formatCurrency(principalPaydown)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', borderTop: '1px solid #e2e8f0', paddingTop: '4px', marginTop: '2px' }}>
                              <span style={{ fontWeight: 700, color: '#0f172a' }}>Total</span>
                              <span style={{ fontWeight: 700, color: '#0f172a' }}>{formatCurrency(totalROIAmount)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}


                {/* Main content with graph selector and insights */}
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left side - Graphs */}
                  <div className="flex-1 space-y-6">
                    {/* Graph Selector */}
                    <div className="grid grid-cols-2 md:flex md:flex-row items-stretch md:items-center gap-2 p-1 bg-muted rounded-lg">
                      <Button
                        variant={selectedProjectionChart === 'equity' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setSelectedProjectionChart('equity')}
                        className="flex-1 h-auto py-2 md:py-1.5 px-2 text-xs md:text-sm"
                      >
                        <span className="truncate md:hidden">Équité</span>
                        <span className="hidden md:inline">Croissance équité</span>
                      </Button>
                      <Button
                        variant={selectedProjectionChart === 'cashflow' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setSelectedProjectionChart('cashflow')}
                        className="flex-1 h-auto py-2 md:py-1.5 px-2 text-xs md:text-sm"
                      >
                        <span className="truncate md:hidden">Cashflow</span>
                        <span className="hidden md:inline">Flux trésorerie</span>
                      </Button>
                      <Button
                        variant={selectedProjectionChart === 'networth' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setSelectedProjectionChart('networth')}
                        className="flex-1 h-auto py-2 md:py-1.5 px-2 text-xs md:text-sm"
                      >
                        <span className="truncate md:hidden">Val. Nette</span>
                        <span className="hidden md:inline">Valeur nette</span>
                      </Button>
                      <Button
                        variant={selectedProjectionChart === 'roi' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setSelectedProjectionChart('roi')}
                        className="flex-1 h-auto py-2 md:py-1.5 px-2 text-xs md:text-sm"
                      >
                        <span className="truncate md:hidden">ROI</span>
                        <span className="hidden md:inline">ROI cumulatif</span>
                      </Button>
                    </div>

                    {/* Selected Chart */}
                    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                      {selectedProjectionChart === 'equity' && (
                        <>
                          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                            Croissance de l'équité sur {limits?.projectionYears || 30} ans
                          </h2>
                          <p className="caption text-muted-foreground mb-6">
                            Évolution de votre capital investi dans la propriété
                          </p>
                          <EquityChart data={mockEquityData} projectionYears={limits?.projectionYears || 30} />
                        </>
                      )}
                      {selectedProjectionChart === 'cashflow' && (
                        <>
                          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                            Flux de trésorerie sur {limits?.projectionYears || 30} ans
                          </h2>
                          <p className="caption text-muted-foreground mb-6">
                            Évolution du cashflow mensuel et annuel
                          </p>
                          <CashflowChart data={mockCashflowData} projectionYears={limits?.projectionYears || 30} />
                        </>
                      )}
                      {selectedProjectionChart === 'networth' && (
                        <>
                          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                            Valeur nette sur {limits?.projectionYears || 30} ans
                          </h2>
                          <p className="caption text-muted-foreground mb-6">
                            Évolution de votre valeur nette (propriété - hypothèque)
                          </p>
                          <NetWorthChart data={mockNetWorthData} projectionYears={limits?.projectionYears || 30} />
                        </>
                      )}
                      {selectedProjectionChart === 'roi' && (
                        <>
                          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                            ROI cumulatif sur {limits?.projectionYears || 30} ans
                          </h2>
                          <p className="caption text-muted-foreground mb-6">
                            Retour sur investissement cumulatif et annuel
                          </p>
                          <ROIChart data={mockROIData} projectionYears={limits?.projectionYears || 30} />
                        </>
                      )}
                    </div>

                  </div>
                </div>

                {/* Projection Table */}
                <div className="mt-6">
                    <ProjectionTable data={projectionData} projectionYears={limits?.projectionYears || 30} />
                </div>
              </div>
            )}

            {activeTab === 'scenarios' && (
              <div className="space-y-6">
                {/* Scenario Cards - Selection */}
                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div>
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                        Scénarios d'analyse
                      </h2>
                      <p className="caption text-muted-foreground">
                        Cliquez sur les scénarios pour les sélectionner et comparer
                      </p>
                    </div>
                    {!isVisitor && (
                      <div className="flex gap-2">
                          <Button
                            variant="outline"
                            iconName="Sparkles"
                            iconPosition="left"
                            onClick={handleAISuggestion}
                          >
                            Suggestion IA
                          </Button>
                        <Button
                          variant="default"
                          iconName="Plus"
                          iconPosition="left"
                          onClick={handleCreateScenario}
                        >
                          Nouveau scénario
                        </Button>
                      </div>
                    )}
                  </div>

                  {loadingScenarios ? (
                        <div className="flex items-center justify-center py-12">
                          <Icon name="Loader2" size={32} className="animate-spin text-muted-foreground" />
                        </div>
                      ) : scenarios?.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-4">
                          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                            <Icon name="GitCompare" size={32} className="text-muted-foreground" />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            Aucun scénario créé
                          </h3>
                          <p className="text-muted-foreground text-center mb-6">
                            Créez votre premier scénario pour comparer différentes options d'investissement
                          </p>
                          {!isVisitor && (
                            <Button
                              variant="default"
                              iconName="Plus"
                              onClick={handleCreateScenario}
                            >
                              Créer un scénario
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                          {scenarios?.map((scenario) => (
                            <ScenarioCard
                              key={scenario?.id}
                              scenario={scenario}
                              onView={isVisitor ? undefined : handleViewScenario}
                              onCompare={isVisitor ? undefined : handleCompareScenario}
                              onDelete={isVisitor ? undefined : handleDeleteScenario}
                              onEdit={isVisitor ? undefined : handleEditScenario}
                              onDuplicate={isVisitor ? undefined : handleDuplicateScenario}
                              isSelected={selectedScenariosForCompare.some(s => s.id === scenario.id)}
                              onToggleSelect={isVisitor ? undefined : handleToggleScenarioSelect}
                            />
                          ))}
                        </div>
                      )}
                </div>

                {/* Comparison View - shows automatically when scenarios are selected */}
                {selectedScenariosForCompare.length >= 1 && (() => {
                  const baseline = getBaselineScenario();
                  const allScenariosToCompare = baseline ? [baseline, ...selectedScenariosForCompare] : selectedScenariosForCompare;

                  // Colors for chart bars
                  const chartColors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

                  return (
                    <div className="bg-card border border-border rounded-xl p-4 md:p-6 space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Icon name="GitCompare" size={24} className="text-primary" />
                          <h2 className="text-xl font-semibold text-foreground">
                            Comparaison ({selectedScenariosForCompare.length} scénario{selectedScenariosForCompare.length > 1 ? 's' : ''})
                          </h2>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="RotateCcw"
                          iconPosition="left"
                          onClick={handleClearSelection}
                        >
                          Réinitialiser
                        </Button>
                      </div>

                      {/* Visual KPI Comparison - Bar Chart Style */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Cashflow Comparison */}
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Icon name="DollarSign" size={16} className="text-success" />
                            Cashflow mensuel
                          </h4>
                          <div className="space-y-2">
                            {allScenariosToCompare.map((scenario, idx) => {
                              const maxCashflow = Math.max(...allScenariosToCompare.map(s => Math.abs(s.monthly_cashflow || 0)));
                              const width = maxCashflow > 0 ? (Math.abs(scenario.monthly_cashflow || 0) / maxCashflow) * 100 : 0;
                              return (
                                <div key={scenario.id} className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-24 truncate">{scenario.name}</span>
                                  <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                                    <div
                                      className="h-full rounded transition-all"
                                      style={{
                                        width: `${width}%`,
                                        backgroundColor: (scenario.monthly_cashflow || 0) >= 0 ? chartColors[idx % chartColors.length] : '#ef4444'
                                      }}
                                    />
                                  </div>
                                  <span className={`text-xs font-semibold w-20 text-right ${(scenario.monthly_cashflow || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                                    {formatCurrency(scenario.monthly_cashflow || 0)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* ROI Comparison */}
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Icon name="TrendingUp" size={16} className="text-primary" />
                            ROI projeté
                          </h4>
                          <div className="space-y-2">
                            {allScenariosToCompare.map((scenario, idx) => {
                              const maxROI = Math.max(...allScenariosToCompare.map(s => Math.abs(s.roi_10_year || 0)));
                              const width = maxROI > 0 ? (Math.abs(scenario.roi_10_year || 0) / maxROI) * 100 : 0;
                              return (
                                <div key={scenario.id} className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-24 truncate">{scenario.name}</span>
                                  <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                                    <div
                                      className="h-full rounded transition-all"
                                      style={{ width: `${width}%`, backgroundColor: chartColors[idx % chartColors.length] }}
                                    />
                                  </div>
                                  <span className="text-xs font-semibold w-20 text-right text-success">
                                    {formatPercent(scenario.roi_10_year || 0)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Investment Comparison */}
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Icon name="Wallet" size={16} className="text-warning" />
                            Investissement initial
                          </h4>
                          <div className="space-y-2">
                            {allScenariosToCompare.map((scenario, idx) => {
                              const maxInv = Math.max(...allScenariosToCompare.map(s => s.total_initial_investment || 0));
                              const width = maxInv > 0 ? ((scenario.total_initial_investment || 0) / maxInv) * 100 : 0;
                              return (
                                <div key={scenario.id} className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-24 truncate">{scenario.name}</span>
                                  <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                                    <div
                                      className="h-full rounded transition-all"
                                      style={{ width: `${width}%`, backgroundColor: chartColors[idx % chartColors.length] }}
                                    />
                                  </div>
                                  <span className="text-xs font-semibold w-20 text-right">
                                    {formatCurrency(scenario.total_initial_investment || 0)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Cash-on-Cash Return */}
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Icon name="Percent" size={16} className="text-accent" />
                            Cash-on-Cash Return
                          </h4>
                          <div className="space-y-2">
                            {allScenariosToCompare.map((scenario, idx) => {
                              const maxCoC = Math.max(...allScenariosToCompare.map(s => Math.abs(s.cash_on_cash_return || 0)));
                              const width = maxCoC > 0 ? (Math.abs(scenario.cash_on_cash_return || 0) / maxCoC) * 100 : 0;
                              return (
                                <div key={scenario.id} className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-24 truncate">{scenario.name}</span>
                                  <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                                    <div
                                      className="h-full rounded transition-all"
                                      style={{ width: `${width}%`, backgroundColor: chartColors[idx % chartColors.length] }}
                                    />
                                  </div>
                                  <span className={`text-xs font-semibold w-20 text-right ${(scenario.cash_on_cash_return || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                                    {formatPercent(scenario.cash_on_cash_return || 0)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Detailed Comparison Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-3 px-2 font-semibold text-muted-foreground">Métrique</th>
                              {allScenariosToCompare.map(scenario => (
                                <th key={scenario.id} className={`text-right py-3 px-2 font-semibold min-w-[120px] ${scenario.isBaseline ? 'text-primary bg-primary/5' : 'text-foreground'}`}>
                                  {scenario.name}
                                  {scenario.isBaseline && (
                                    <span className="block text-xs font-normal text-muted-foreground">Référence</span>
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-border/50 bg-muted/30">
                              <td className="py-3 px-2 font-semibold text-foreground" colSpan={allScenariosToCompare.length + 1}>Rendement</td>
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Cashflow mensuel</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold ${scenario.isBaseline ? 'bg-primary/5' : ''} ${(scenario.monthly_cashflow || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                                  {formatCurrency(scenario.monthly_cashflow || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Cashflow annuel</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold ${scenario.isBaseline ? 'bg-primary/5' : ''} ${((scenario.monthly_cashflow || 0) * 12) >= 0 ? 'text-success' : 'text-destructive'}`}>
                                  {formatCurrency((scenario.monthly_cashflow || 0) * 12)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">NOI annuel</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {formatCurrency(scenario.noi || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Taux de capitalisation</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {formatPercent(scenario.cap_rate || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Cash-on-Cash Return</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold ${scenario.isBaseline ? 'bg-primary/5' : ''} ${(scenario.cash_on_cash_return || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                                  {formatPercent(scenario.cash_on_cash_return || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">ROI projeté</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-success ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {formatPercent(scenario.roi_10_year || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">DSCR</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold ${scenario.isBaseline ? 'bg-primary/5' : ''} ${(scenario.dscr || 0) >= 1.25 ? 'text-success' : (scenario.dscr || 0) >= 1 ? 'text-warning' : 'text-destructive'}`}>
                                  {(scenario.dscr || 0).toFixed(2)}x
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50 bg-muted/30">
                              <td className="py-3 px-2 font-semibold text-foreground" colSpan={allScenariosToCompare.length + 1}>Financement</td>
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Investissement initial</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {formatCurrency(scenario.total_initial_investment || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Mise de fonds</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {formatCurrency(scenario.down_payment_amount || 0)}
                                  <span className="text-xs text-muted-foreground ml-1">
                                    ({(scenario.down_payment_percent || 0).toFixed(1)}%)
                                  </span>
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Montant hypothécaire</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {formatCurrency(scenario.mortgage_amount || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Taux d'intérêt</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {scenario.interest_rate || 0}%
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-border/50">
                              <td className="py-3 px-2 text-muted-foreground">Amortissement</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {scenario.amortization_period || 25} ans
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="py-3 px-2 text-muted-foreground">Paiement mensuel</td>
                              {allScenariosToCompare.map(scenario => (
                                <td key={scenario.id} className={`py-3 px-2 text-right font-semibold text-foreground ${scenario.isBaseline ? 'bg-primary/5' : ''}`}>
                                  {formatCurrency(scenario.monthly_payment || 0)}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'photos' && (
              <PhotoGallery photos={propertyPhotos} />
            )}

            {activeTab === 'dossier' && (
              <DossierTab
                property={property}
                user={user}
                subscriptionTier={subscriptionTier}
              />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Icon name="Info" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Aucune analyse disponible</p>
            </div>
          </div>
        )}

        {/* CTA visiteur : inviter à créer sa propre analyse */}
        {isVisitor && property && (
          <div
            className="mt-12 py-10 px-6 rounded-xl text-center"
            style={{ background: 'var(--color-muted)' }}
          >
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Vous aimez cette analyse ?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Créez votre propre analyse en quelques clics, gratuitement.
            </p>
            <Button
              variant="primary"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => navigate('/property-analysis-wizard')}
            >
              Analyser mon immeuble
            </Button>
          </div>
        )}
      </main>
      {showSubscriptionGate && (
        <SubscriptionGate
          requiredTier="pro"
          featureName="l'analyse IA et l'export PDF"
          onUpgrade={handleUpgrade}
          onClose={() => setShowSubscriptionGate(false)}
        />
      )}
      <CurrencySelector />



      {/* Share Modal (owner only) */}
      {!isVisitor && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          property={property}
          shareUrl={getShareUrl()}
          onUnshare={async () => {
            await handleUnshare();
            setShowShareModal(false);
          }}
        />
      )}



    </div>
  );
};

export default AnalysisResults;